import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Eye, Lightbulb, ShieldCheck } from "lucide-react";
import { T } from "@/components/editable/T";

const DEFAULT_MEETING_URL = "https://meetings-eu1.hubspot.com/joost-kuijf/plan-een-demo-round-robin?embed=true";
const EN_MEETING_URL = "https://meetings-eu1.hubspot.com/joost-kuijf/plan-een-demo-round-robin-english?embed=true";
const DE_MEETING_URL = "https://meetings-eu1.hubspot.com/jonathan-cramer/demo-runde-planen?embed=true";

const benefits = [
  { icon: Eye, key: "benefit1", defaultTitle: "Ervaar hoe het werkt", defaultDesc: "Krijg een persoonlijke demo en ontdek hoe dit in jouw organisatie werkt." },
  { icon: Lightbulb, key: "benefit2", defaultTitle: "Krijg deskundige begeleiding", defaultDesc: "Learned is méér dan software. Ons team van specialisten helpt je bij de implementatie en adoptie." },
  { icon: ShieldCheck, key: "benefit3", defaultTitle: "Geen verplichting", defaultDesc: "Een vrijblijvend gesprek om te ontdekken hoe we jouw organisatie kunnen helpen." },
];

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: IdleRequestCallback,
    options?: IdleRequestOptions,
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
};

type ScheduleDemoProps = {
  compactMobile?: boolean;
  /** Translation key for the section heading (within the scheduleDemo namespace) */
  titleKey?: string;
  titleDefault?: string;
  /** Alignment of the section header */
  align?: "center" | "left";
};

export const ScheduleDemo = ({
  compactMobile = false,
  titleKey = "title",
  titleDefault = "Klaar om te beginnen?",
  align = "center",
}: ScheduleDemoProps) => {
  const { lang = "nl" } = useParams<{ lang: string }>();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const meetingUrl = lang === "en" ? EN_MEETING_URL : lang === "de" ? DE_MEETING_URL : DEFAULT_MEETING_URL;

  useEffect(() => {
    const start = () => setIsVisible(true);
    const idleWindow = window as IdleWindow;

    if (idleWindow.requestIdleCallback) {
      const id = idleWindow.requestIdleCallback(start, { timeout: 2000 });
      return () => {
        if (idleWindow.cancelIdleCallback) {
          idleWindow.cancelIdleCallback(id);
        }
      };
    }

    const id = setTimeout(start, 500);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    const mo = new MutationObserver(() => {
      const container = document.querySelector(".meetings-iframe-container");
      const iframe = container?.querySelector("iframe");
      if (iframe) {
        iframe.setAttribute("tabindex", "-1");
        mo.disconnect();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      try {
        document.body.removeChild(script);
      } catch {
        // no-op
      }
    };
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      id="schedule-demo"
      className={`relative overflow-hidden ${compactMobile ? "pt-8 pb-8 md:py-20" : "py-10 md:py-20"}`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-32 -right-32 h-[600px] w-[600px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #9055EE 0%, transparent 70%)" }}
        />
        <div
          className="absolute top-1/2 -left-48 h-[500px] w-[500px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #F97316 0%, #FBBF24 40%, transparent 70%)" }}
        />
        <div
          className="absolute top-0 -left-20 h-[600px] w-[600px] rounded-full opacity-20 blur-2xl"
          style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 70%)" }}
        />
      </div>
      <div className={`relative z-10 px-4 ${align === "left" ? "w-full sm:w-[80%] max-w-7xl mx-auto" : "container mx-auto max-w-6xl"}`}>
        <div className={`${align === "left" ? "text-left" : "text-center"} animate-fade-in ${compactMobile ? "mb-6 md:mb-16" : "mb-16"}`}>
          <h2 className="feature-section-heading mb-4 text-foreground">
            <T page="scheduleDemo" k={titleKey} d={titleDefault} />
          </h2>
          <p className={`text-lead max-w-2xl text-muted-foreground ${align === "left" ? "" : "mx-auto"}`}>
            <T page="scheduleDemo" k="subtitle" d="Plan een demo en zie hoe Learned het functiehuis, Performance Management en beloningsbeleid verbindt." />
          </p>
        </div>

        <div className="mx-auto -mx-4 flex max-w-5xl flex-col gap-10 sm:mx-auto">
          <div className="hidden grid-cols-1 gap-8 md:grid md:grid-cols-3">
            {benefits.map(({ icon: Icon, key, defaultTitle, defaultDesc }) => (
              <div key={key} className="flex items-start gap-4 animate-fade-in">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#F3EDFD]">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <T page="scheduleDemo" k={`benefits.${key}.title`} d={defaultTitle} as="h3" className="mb-1 text-base font-semibold text-foreground [&>p]:m-0 [&>p]:text-base [&>p]:font-semibold" />
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    <T page="scheduleDemo" k={`benefits.${key}.description`} d={defaultDesc} />
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-fade-in min-w-0 flex-1 overflow-visible" style={{ animationDelay: "200ms" }}>
            <div
              className={`meetings-iframe-container w-full [&>iframe]:!w-full [&>iframe]:!max-w-full [&>iframe]:!border-0 [&>iframe]:!overflow-hidden ${compactMobile ? "min-h-[560px] md:min-h-[700px]" : "min-h-[700px]"}`}
              data-src={meetingUrl}
            />
          </div>
        </div>

      </div>
    </section>
  );
};
